import React, { useState, useEffect } from "react";
import api from "../utils/api";
import apiV2, { orgApi } from "../utils/apiV2";
import getTicketLink from "../utils/ticketLink";
import { Button } from "../components/ui/button";
import { 
  Zap, 
  Calendar, 
  Users, 
  Package, 
  LogOut, 
  Settings, 
  Activity, 
  HelpCircle, 
  MapPin,
  Menu,
  X,
  AlertTriangle,
  DollarSign,
  Sparkles,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  LayoutDashboard,
  Bell,
  Search,
  User,
  CreditCard,
  Sliders,
  Shield,
  BookOpen,
  FileText,
  Star,
  Download,
  TrendingUp,
  QrCode,
  CheckCircle,
  Copy,
  Mail,
  Phone,
  Link2,
  Check,
  Hexagon
} from "lucide-react";
import { toast } from "sonner";
import { trackActivity } from "../utils/trackActivity";
import { useNavigate, useSearchParams } from "react-router-dom";
// Clerk disabled - using custom JWT auth
// import { useClerk } from '@clerk/react-router';
import SettingsDialog from "../components/SettingsDialog";
import TeamMembersTab from "../components/TeamMembersTab";
import { navigationGroups, filterNavigationByPermissions } from "../config/navigation";
import { hasAnyIntelAccess } from "../utils/intelPerms";
import { jwtDecode } from 'jwt-decode';

import AssetsTab from "../components/AssetsTab";
import SystemHealthTabSimple from "../components/SystemHealthTabSimple";
import OperationsDashboard from "../components/OperationsDashboard";
import HomeActivity from "../components/HomeActivity";
import CompanyHealthTab from "../components/CompanyHealthTab";
import GlobalSearch from "../components/GlobalSearch";
import HelpPage from "../components/HelpPage";
import AssignmentsTab from "../components/AssignmentsTab";
import CalendarScheduler from "../components/CalendarScheduler";
import NotificationBell from "../components/NotificationBell";
import ApprovalReviewDialog from "../components/ApprovalReviewDialog";
import BrandingTab from "../components/BrandingTab";
import WaiversTabClean from "../components/WaiversTabClean";
import ReviewsTab from "../components/ReviewsTab";
import CheckInTab from "../components/CheckInTab";
import RelationsTab from "../components/RelationsTab";
import NotificationsTab from "../components/NotificationsTab";
import SettingsTabEnhanced from "../components/SettingsTabEnhanced";
import WidgetCustomization from "../components/WidgetCustomizationNew";
import ProfitLossTab from "../components/ProfitLossTab";
import ProfileTab from "../components/ProfileTab";
import PaymentsTabSimple from "../components/PaymentsTabSimple";
import PartnersAndPromosTab from "../components/PartnersAndPromosTab";
import DiscountsTab from "../components/DiscountsTab";
import GiftCardsTab from "../components/GiftCardsTab";
import SharedServicesTab from "../components/SharedServicesTab";
import OnboardingTutorial from "../components/OnboardingTutorial";
import TourLauncher from "../components/TourLauncher";
import SettingsTab from "../components/SettingsTab";
import RolesTab from "../components/RolesTab";
import ActionsBalance from "../components/ActionsBalance";
import SubscriptionSuccess from "../components/SubscriptionSuccess";
import ServicesTabImproved from "../components/ServicesTabImproved";
import MiniFlowsTab from "../components/MiniFlowsTab";
import RevenueTab from "../components/RevenueTab";
import ExpensesTab from "../components/ExpensesTab";
import ReportsTab from "../components/ReportsTab";
// PromoCodesTab merged into DiscountsTab (under Customers)
// PayoutsTab + AccountTab merged into PaymentsTabSimple (under Financials)
import TimeclockTab from "../components/TimeclockTab";
import MarketplaceTab from "../components/MarketplaceTab";
import BlogTab from "../components/BlogTab";
import ConversionsTab from "../components/ConversionsTab";
import DistributionTab from "../components/DistributionTab";
import AdvertisingTab from "../components/AdvertisingTab";
import ScheduleTab from "../components/ScheduleTab";
import PartnersTab from "../components/PartnersTab";
import { BACKEND_URL } from "../config/api";
import useSetupProgress from "../hooks/useSetupProgress";


// BACKEND_URL imported from config/api
const API = `${BACKEND_URL}/api`;

const DashboardV2 = () => {
  const navigate = useNavigate();
  // Clerk disabled - using custom JWT auth
  // const clerk = useClerk();
  
  // Handle payment success redirect from Stripe
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [editBookingId, setEditBookingId] = useState(null);
  const [autoOpenAddBooking, setAutoOpenAddBooking] = useState(false);
  const [partnerConnectLoading, setPartnerConnectLoading] = useState(false);
  
  // Handle tab query parameter for deep linking (e.g., from Stripe Connect return)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const connectSuccess = searchParams.get('connect_success');
    const connectedPartnerId = searchParams.get('connected');
    const refreshPartners = searchParams.get('refresh');
    const editBookingParam = searchParams.get('editBookingId');
    const action = searchParams.get('action');
    
    // Skip if we're handling the connect action (handled by other useEffect)
    if (action === 'connect') {
      // Still set the active view to partners while connecting
      setActiveView('partners');
      return;
    }
    
    if (tabParam) {
      // Valid tab views that can be navigated to
      const validTabs = ['overview', 'home', 'marketplace', 'schedule', 'mySchedule', 'assignments', 'roles', 'teamMembers', 'assets', 'services', 
        'flows', 'miniFlows', 'profitLoss', 'discounts', 'account', 'payments', 'payouts', 
        'branding', 'waivers', 'notifications', 'reviews', 'partners',
        'checkIn', 'settings', 'help', 'systemHealth', 'companyHealth', 'relations', 'checkin',
        'distribution', 'conversions', 'advertising', 'blog', 'giftCards', 'reports'];
      
      if (validTabs.includes(tabParam)) {
        setActiveView(tabParam);
        
        // Handle edit booking navigation from Schedule tab
        if (editBookingParam && tabParam === 'schedule') {
          setEditBookingId(editBookingParam);
        }
        
        // Show success message for Stripe Connect return (for affiliates)
        if (connectSuccess === 'true' && tabParam === 'discounts') {
          toast.success('Stripe Connect onboarding completed! The affiliate status will update shortly.');
        }
        
        // Show success message for partner Stripe Connect return
        if (connectedPartnerId && tabParam === 'partners') {
          toast.success('Stripe account connected successfully! Your partner status will update shortly.');
        }
        
        // Clear URL params after processing
        setSearchParams({});
      }
    }
  }, [searchParams, setSearchParams]);
  
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const bookingId = searchParams.get('booking_id');
    const confirmationCode = searchParams.get('confirmation_code');
    
    if (paymentSuccess === 'true' && bookingId) {
      // Fetch booking details and show confirmation modal
      const fetchBookingDetails = async () => {
        try {
          // Try authenticated endpoint first
          const response = await api.get(`/bookings/${bookingId}`);
          if (response.data) {
            setConfirmedBooking({
              ...response.data,
              confirmationCode: confirmationCode || response.data.confirmationCode
            });
            setShowPaymentSuccessModal(true);
            return;
          }
        } catch (err) {
          // Primary fetch failed, trying fallback
        }
        
        // Fallback: Try public trips endpoint with confirmation code
        if (confirmationCode) {
          try {
            const publicResponse = await api.get(`/public/trips/${confirmationCode}`);
            if (publicResponse.data) {
              setConfirmedBooking({
                ...publicResponse.data,
                confirmationCode: confirmationCode
              });
              setShowPaymentSuccessModal(true);
              return;
            }
          } catch (publicErr) {
            // Public trips fallback also failed
          }
        }
        
        // Final fallback: Show basic confirmation with just the code
        setConfirmedBooking({
          confirmationCode: confirmationCode || 'N/A',
          bookingId: bookingId
        });
        setShowPaymentSuccessModal(true);
      };
      fetchBookingDetails();
      
      // Clear URL params
      setSearchParams({});
    }
    
    const paymentCancelled = searchParams.get('payment_cancelled');
    if (paymentCancelled === 'true') {
      toast.error('Payment was cancelled');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Get organization context
  const organizationName = localStorage.getItem('organizationName') || 'BookingFlows';
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userPermissions = userData.permissions || [];
  const userType = localStorage.getItem('userType') || 'admin';
  
  // Get organization branding (color, logo)
  const [orgBranding, setOrgBranding] = useState({
    primaryColor: '#007AFF',
    logoUrl: null
  });
  
  // Decode JWT to get tabPermissions and user info.
  // Decoded SYNCHRONOUSLY (lazy initializers) so permission-gated children
  // (e.g. the Manifest) never render one frame with admin defaults.
  const decodeAuth = () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const storedUserType = localStorage.getItem('userType');
      if (token && storedUserType === 'teamMember') {
        const decoded = jwtDecode(token);
        const role = (decoded.role || '').toLowerCase();
        if (role === 'owner' || role === 'admin' || role.includes('owner')) {
          return { isAdmin: true, perms: {}, uid: decoded.teamId || decoded.userId || null };
        }
        return { isAdmin: false, perms: decoded.tabPermissions || {}, uid: decoded.teamId || decoded.userId || null };
      }
      if (token) {
        const decoded = jwtDecode(token);
        return { isAdmin: true, perms: {}, uid: decoded.userId || null };
      }
    } catch (e) { /* invalid token — fall through to defaults */ }
    return { isAdmin: true, perms: {}, uid: null };
  };
  const [tabPermissions, setTabPermissions] = useState(() => decodeAuth().perms);
  const [isAdmin, setIsAdmin] = useState(() => decodeAuth().isAdmin);
  const [currentUserId, setCurrentUserId] = useState(() => decodeAuth().uid);
  const [organizationId, setOrganizationId] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    const storedUserType = localStorage.getItem('userType');
    
    if (token && storedUserType === 'teamMember') {
      try {
        const decoded = jwtDecode(token);
        const role = (decoded.role || '').toLowerCase();
        
        // If the team member is actually an owner/admin, treat them as admin
        if (role === 'owner' || role === 'admin' || role.includes('owner')) {
          setIsAdmin(true);
          setCurrentUserId(decoded.teamId || decoded.userId); // prefer TeamMember record ID for booking filters
          setOrganizationId(decoded.organizationId);
        } else {
          // Regular team members see limited tabs based on permissions
          const permissions = decoded.tabPermissions || {};
          setTabPermissions(permissions);
          setIsAdmin(false);
          // Use teamId (TeamMember record ID) for "See My Bookings" filter — bookings store teamMemberId
          setCurrentUserId(decoded.teamId || decoded.userId);
          setOrganizationId(decoded.organizationId);
        }
      } catch (error) {
        // Token decode error - user may need to re-login
      }
    } else if (token) {
      try {
        const decoded = jwtDecode(token);
        setIsAdmin(true); // Admins see everything
        setCurrentUserId(decoded.userId || null);
        setOrganizationId(decoded.organizationId); // Get organization ID from token
      } catch (error) {
        // Token decode error
      }
    }
  }, [navigate]);
  
  // Handle partner Stripe Connect onboarding from email link
  // This useEffect must be after organizationId state is declared
  useEffect(() => {
    const action = searchParams.get('action');
    const partnerId = searchParams.get('partner_id');
    const tabParam = searchParams.get('tab');
    
    if (action === 'connect' && partnerId && tabParam === 'partners' && organizationId) {
      // Auto-trigger Stripe Connect onboarding for the partner
      const initiateStripeConnect = async () => {
        setPartnerConnectLoading(true);
        try {
          const response = await apiV2.post(
            `/organizations/${organizationId}/partners/${partnerId}/create-onboarding-link`
          );
          
          if (response.data.success && response.data.onboarding_url) {
            // Redirect to Stripe onboarding
            toast.success('Redirecting to Stripe to connect your account...');
            window.location.href = response.data.onboarding_url;
          } else {
            toast.error('Failed to generate Stripe onboarding link');
            setSearchParams({ tab: 'partners' });
          }
        } catch (error) {
          console.error('Stripe Connect error:', error);
          toast.error(error.response?.data?.detail || 'Failed to start Stripe onboarding');
          setSearchParams({ tab: 'partners' });
        } finally {
          setPartnerConnectLoading(false);
        }
      };
      
      initiateStripeConnect();
    }
  }, [searchParams, organizationId, setSearchParams]);
  
  // Detect scroll to add shadow
  useEffect(() => {
    const handleScroll = (e) => {
      const scrolled = e.target.scrollTop > 10;
      setIsScrolled(scrolled);
    };
    
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.addEventListener('scroll', handleScroll);
      return () => mainContent.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  // Fetch organization branding
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        // Use v2 PostgreSQL API for organization details
        const response = await orgApi.getOrganization();
        if (response.data) {
          setOrgBranding({
            primaryColor: response.data.primaryColor || '#007AFF',
            logoUrl: response.data.logoUrl
          });
        }
      } catch (error) {
        console.error('Error fetching branding:', error);
      }
    };
    
    fetchBranding();
  }, []);
  
  // Helper function to check if user has permission
  const hasPermission = (permission) => {
    // Admins have all permissions
    if (userType === 'admin') return true;
    // Check if team member has specific permission
    return userPermissions.includes(permission);
  };
  
  // Check for payment success/canceled in URL
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get('payment');
  const sessionId = urlParams.get('session_id');
  const showSubscriptionSuccess = paymentStatus === 'success' && sessionId;
  
  const [activeView, setActiveView] = useState(() => {
    // Restore last active tab from localStorage
    return localStorage.getItem('lastActiveTab') || 'overview';
  });
  
  // Filter state for Relations tab (set when navigating from Dashboard)
  const [relationsFilter, setRelationsFilter] = useState('all');
  
  // Navigation handler that supports passing filter state
  const handleNavigate = (view, options = {}) => {
    if (view === 'relations' && options.filter) {
      setRelationsFilter(options.filter);
    }
    // Deep-link from Home/Manifest smart search → open the EditBookingModal in Schedule
    if (options.editBookingId) {
      setEditBookingId(options.editBookingId);
    }
    // "+ New booking" from the Manifest → jump to Calendar and auto-open the Add Booking wizard
    if (options.openAddBooking) {
      setAutoOpenAddBooking(true);
    }
    setActiveView(view);
  };
  
  // Save active tab to localStorage whenever it changes + audit breadcrumb
  useEffect(() => {
    localStorage.setItem('lastActiveTab', activeView);
    trackActivity('navigate', activeView);
  }, [activeView]);
  // Sidebar state - persists to localStorage
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) {
      return saved === 'true';
    }
    // Default: open on desktop, closed on mobile
    return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true;
  });
  
  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', sidebarOpen.toString());
  }, [sidebarOpen]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [trips, setBookings] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState(null);
  const setupProgress = useSetupProgress({ enabled: isAdmin });
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);
  
  // Collapsed sections - persist to localStorage
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const saved = localStorage.getItem('collapsedSections');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure internal is collapsed by default if not explicitly set
      if (parsed.internal === undefined) {
        parsed.internal = true;
      }
      return parsed;
    }
    return {
      operations: true, // Default: collapsed - only expand when navigating to section
      financials: true,
      employees: true, // Default: collapsed
      customers: true,
      management: true,
      bookings: true,
      internal: true  // Default: collapsed
    };
  });
  
  // Save collapsed sections to localStorage
  useEffect(() => {
    localStorage.setItem('collapsedSections', JSON.stringify(collapsedSections));
  }, [collapsedSections]);
  
  // Auto-expand section containing current activeView
  useEffect(() => {
    const findGroupForView = (viewId) => {
      for (const group of navigationGroups) {
        if (group.items) {
          if (group.items.find(item => item.id === viewId)) {
            return group.id;
          }
        }
      }
      return null;
    };
    
    const groupId = findGroupForView(activeView);
    if (groupId && collapsedSections[groupId]) {
      setCollapsedSections(prev => ({
        ...prev,
        [groupId]: false // Expand the section containing current view
      }));
    }
  }, [activeView]);
  
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if user has seen onboarding - REMOVED auto-show
  // User can manually trigger it with the "Quick Tour" button
  useEffect(() => {
    // Don't auto-show onboarding anymore
    // Users can click the "Quick Tour" button when they want it
  }, []);

  // Handle window resize - removed auto-close behavior
  // Sidebar state now persists via localStorage
  // Users manually control sidebar with toggle button

  useEffect(() => {
    fetchData();
    // Poll every 30 seconds
    const interval = setInterval(fetchData, 30000);
    
    // Listen for navigation events from components
    const handleNavigate = (e) => {
      const targetView = e.detail;
      setActiveView(targetView);
      
      // Expand the Operations section if navigating to operations tabs
      const operationsTabs = ['roles', 'teamMembers', 'assets'];
      if (operationsTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, operations: false }));
      }
      
      // Expand Financials section
      const financialTabs = ['payroll', 'payouts', 'financialOverview', 'partners'];
      if (financialTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, financials: false }));
      }
      
      // Expand Customer section as needed
      const customerTabs = ['services', 'branding', 'waivers', 'customers', 'reviews'];
      if (customerTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, bookings: false }));
      }
      
      // Expand Employees section
      const employeeTabs = ['timeclock', 'worksites'];
      if (employeeTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, employees: false }));
      }
    };
    window.addEventListener('navigate', handleNavigate);

    // Guided-tour accordion: open ONLY the sidebar group the tour is entering
    const handleTourSetGroup = (e) => {
      const gid = e.detail;
      setCollapsedSections({
        main: gid !== 'main',
        operations: gid !== 'operations',
        financials: gid !== 'financials',
        customers: gid !== 'customers',
        marketing: gid !== 'marketing',
        internal: true,
      });
    };
    window.addEventListener('tour-set-group', handleTourSetGroup);
    
    // Listen for help guide open events
    const handleOpenHelpGuide = (e) => {
      const section = e.detail?.section || 'setup';
      // Legacy compatibility: old 'welcome'/'overview' both map to setup home
      const mapped = section === 'welcome' || section === 'overview' ? 'setup' : section;
      setHelpSection(mapped);
      setHelpOpen(true);
    };
    window.addEventListener('openHelpGuide', handleOpenHelpGuide);

    // Listen for settings-open events (e.g. owner "Change in Settings" links)
    const handleOpenSettings = () => {
      setSettingsOpen(true);
    };
    window.addEventListener('openSettings', handleOpenSettings);

    // Guided tour closes the dialog when sailing on
    const handleCloseSettings = () => {
      setSettingsOpen(false);
    };
    window.addEventListener('closeSettings', handleCloseSettings);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('navigate', handleNavigate);
      window.removeEventListener('tour-set-group', handleTourSetGroup);
      window.removeEventListener('openHelpGuide', handleOpenHelpGuide);
      window.removeEventListener('openSettings', handleOpenSettings);
      window.removeEventListener('closeSettings', handleCloseSettings);
    };
  }, []);

  const fetchData = async () => {
    try {
      // Use v2 PostgreSQL API endpoints
      // Use Promise.allSettled to handle permission errors gracefully
      const [teamMembersRes, assetsRes, tripsRes] = await Promise.allSettled([
        orgApi.getTeamMembers(),
        orgApi.getAssets(false),  // include INACTIVE so the Assets tab can show them greyed (not drop them)
        orgApi.getBookings(),
      ]);
      
      // Extract data from settled promises, handling permission errors gracefully
      const teamData = teamMembersRes.status === 'fulfilled' 
        ? (teamMembersRes.value.data?.teamMembers || teamMembersRes.value.data || [])
        : [];
      
      const assetsData = assetsRes.status === 'fulfilled'
        ? (assetsRes.value.data?.assets || assetsRes.value.data || [])
        : [];
      
      const bookingsData = tripsRes.status === 'fulfilled'
        ? (tripsRes.value?.bookings || tripsRes.value?.data?.bookings || []).map(b => ({
            ...b,
            preferredDate: b.bookingDate,
            preferredTime: b.startTime,
            servicePrice: b.totalPrice
          }))
        : [];
      
      setTeamMembers(teamData);
      setAssets(assetsData);
      setBookings(bookingsData);
      
      // Only show error if ALL requests failed (not just permission denied)
      const allFailed = [teamMembersRes, assetsRes, tripsRes].every(r => r.status === 'rejected');
      if (allFailed) {
        toast.error("Failed to load data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Set empty arrays on error to prevent undefined issues
      setTeamMembers([]);
      setAssets([]);
      setBookings([]);
      toast.error("Failed to load data");
    }
  };

  const handleLogout = async () => {
    try {
      // Audit breadcrumb before we drop the token (keepalive ensures delivery)
      trackActivity('logout', null, 'Logged out');
      // Clear all auth data including JWT
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("userType");
      localStorage.removeItem("userData");
      localStorage.removeItem("organizationId");
      localStorage.removeItem("organizationSlug");
      localStorage.removeItem("organizationName");
      localStorage.removeItem("clerkAuth");
      
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local storage
      localStorage.clear();
      navigate("/login");
    }
  };

  const handleSearchNavigate = (tab, itemId) => {
    setActiveView(tab);
    toast.success(`Navigated to ${tab}`);
  };

  const handleHelpClick = (section) => {
    setHelpSection(section);
    setHelpOpen(true);
  };

  const handleViewRequests = (requests) => {
    setPendingRequests(requests);
    setApprovalDialogOpen(true);
  };

  const handleApprovalComplete = () => {
    fetchData(); // Refresh team_member data after approval
  };
  
  const handleSimulateDemo = async () => {
    try {
      setDemoLoading(true);
      const response = await api.post(`/simulate-light-show`);
      toast.success(
        `Demo data created! ${response.data.summary.assets} assets, ${response.data.summary.team_members} team_members, ${response.data.summary.trips} trips.`,
        { duration: 6000 }
      );
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error simulating demo:', error);
      toast.error('Failed to create demo data');
    } finally {
      setDemoLoading(false);
    }
  };

  // Filter navigation based on user type and permissions
  const navGroups = filterNavigationByPermissions(tabPermissions, isAdmin);
  
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return (
    <>
      {/* Partner Stripe Connect Loading Overlay */}
      {partnerConnectLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center max-w-md mx-4">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connecting to Stripe</h2>
            <p className="text-gray-600">Please wait while we redirect you to Stripe to complete your account setup...</p>
          </div>
        </div>
      )}
      
      {/* Payment Success Modal - Shows when returning from Stripe checkout */}
      {showPaymentSuccessModal && confirmedBooking && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={() => setShowPaymentSuccessModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header - Green like "Already Paid" */}
            <div className="p-6 text-center bg-gradient-to-r from-green-500 to-green-600">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h2>
              <p className="text-white text-opacity-90">Payment received - Customer notified</p>
            </div>

            {/* Booking Details */}
            <div className="p-6 space-y-4">
              {/* Confirmation Code */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Confirmation Code</p>
                <p className="text-3xl font-mono font-bold text-blue-600">{confirmedBooking.confirmationCode}</p>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                  <p className="text-sm font-semibold text-gray-900">{confirmedBooking.customerName || 'N/A'}</p>
                  {confirmedBooking.customerEmail && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3" />
                      {confirmedBooking.customerEmail}
                    </p>
                  )}
                  {confirmedBooking.customerPhone && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />
                      {confirmedBooking.customerPhone}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {confirmedBooking.preferredDate 
                      ? new Date(confirmedBooking.preferredDate + 'T00:00:00').toLocaleDateString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric' 
                        })
                      : 'N/A'}
                  </p>
                  {confirmedBooking.preferredTime && (
                    <p className="text-sm text-gray-600">{confirmedBooking.preferredTime}</p>
                  )}
                </div>

                {confirmedBooking.serviceName && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</p>
                    <p className="text-sm font-semibold text-gray-900">{confirmedBooking.serviceName}</p>
                  </div>
                )}

                {confirmedBooking.groupSize && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Capacity</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {confirmedBooking.groupSize} {confirmedBooking.groupSize === 1 ? 'person' : 'people'}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payment</p>
                  <p className="text-sm font-semibold text-green-600">Paid</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-4">
                <Button
                  variant="outline"
                  onClick={async () => {
                    const link = await getTicketLink(confirmedBooking.confirmationCode);
                    navigator.clipboard.writeText(link);
                    toast.success('Ticket link with magic access copied!');
                  }}
                  className="w-full"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Ticket Link
                </Button>

                <Button
                  variant="outline"
                  onClick={async () => {
                    const url = await getTicketLink(confirmedBooking.confirmationCode);
                    window.open(url, '_blank');
                  }}
                  className="w-full"
                >
                  View Ticket
                </Button>

                <Button
                  onClick={() => setShowPaymentSuccessModal(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Show subscription success screen if payment successful */}
      {showSubscriptionSuccess && <SubscriptionSuccess />}
      
      {!showSubscriptionSuccess && (
    <div className="flex h-screen font-['Manrope',sans-serif] bg-white">
      <div className="flex w-full gap-0">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Collapsible with width */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0 lg:w-64'
        } bg-[#FBFCFF] border-r border-slate-200/60 fixed lg:relative transition-all duration-300 flex flex-col h-full z-50 overflow-hidden max-w-[16rem]`}
        style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}
      >
        {/* Sidebar Header */}
        <div className="px-4 pt-8 md:pt-6 pb-4" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top, 1.5rem))' }}>
          {/* Mobile Close Button — top-right corner, small */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-1 right-1 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 transition-colors hover:bg-slate-200"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-center gap-2.5 min-w-0">
            <img 
              src="/logo-icon.png" 
              alt="BookingFlows" 
              className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
            />
            <span className="flex flex-col items-start leading-none min-w-0">
              <span className="font-bold text-sm tracking-tight text-slate-900 truncate w-full">
                {organizationName}
              </span>
              <span className="text-[9px] font-semibold text-slate-400 tracking-wide mt-0.5">by BookingFlows</span>
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 pb-4 space-y-1 overflow-y-auto scrollbar-hide">
          {navGroups.map((group) => {
            const isCollapsed = collapsedSections[group.id];
            
            // Handle standalone tabs (like Home)
            if (group.isStandalone) {
              const isActive = activeView === group.id;
              return (
                <button
                  key={group.id}
                  data-testid={`nav-${group.id}`}
                  onClick={() => setActiveView(group.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-left transition-colors duration-150 ${
                    isActive
                      ? 'bg-[#EBF2FF] text-[#2563EB] font-bold'
                      : 'text-slate-700 hover:bg-slate-100/80 font-semibold'
                  }`}
                >
                  <span className="truncate">{group.label}</span>
                </button>
              );
            }
            
            // Collapsible groups
            return (
              <div key={group.id} className="space-y-1">
                {/* Group header - clickable to expand/collapse */}
                <button
                  data-testid={`nav-group-${group.id}`}
                  onClick={() => toggleSection(group.id)}
                  className="w-full flex items-center justify-between px-4 pt-5 pb-1.5 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400 hover:text-slate-600 transition-colors duration-150"
                >
                  <span>{group.label}</span>
                  {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                
                {/* Group Items - shown when not collapsed */}
                {!isCollapsed && group.items && group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeView === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      data-testid={`nav-${item.id}`}
                      onClick={() => setActiveView(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-left transition-colors duration-150 ${
                        isActive
                          ? 'bg-[#EBF2FF] text-[#2563EB] font-bold'
                          : 'text-slate-700 hover:bg-slate-100/80 font-semibold'
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-150 ${
                        isActive ? 'bg-[#2563EB] text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <Icon className="w-4 h-4" strokeWidth={1.75} />
                      </span>
                      <span className="truncate">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="ml-auto px-2 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
        
        {/* Settings & Help at bottom of sidebar - FIXED POSITION */}
        <div className="px-3 pb-4 space-y-1 pt-3 flex-shrink-0 border-t border-slate-100/70">
          {/* Intelligence — admins + team members with intel permissions */}
          {(isAdmin || hasAnyIntelAccess()) && (
            <button
              onClick={() => navigate('/intelligence')}
              data-testid="nav-intelligence"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-[#EBF2FF] transition-colors duration-150 group"
            >
              <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2563EB] to-indigo-500 text-white flex items-center justify-center shrink-0 shadow-sm shadow-blue-200">
                <Hexagon className="w-4 h-4" strokeWidth={1.75} />
              </span>
              <span className="truncate">Intelligence</span>
              <Sparkles className="ml-auto w-4 h-4 text-[#2563EB]" />
            </button>
          )}
          {/* Settings - Only show for admins */}
          {isAdmin && (
            <button
              onClick={() => setSettingsOpen(true)}
              data-testid="sidebar-settings-button"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100/80 transition-colors duration-150"
            >
              <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                <Settings className="w-4 h-4" strokeWidth={1.75} />
              </span>
              <span>Settings</span>
            </button>
          )}
          {/* My Profile - Only show for team members */}
          {!isAdmin && (
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100/80 transition-colors duration-150"
              data-testid="sidebar-settings-button"
            >
              <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                <User className="w-4 h-4" strokeWidth={1.75} />
              </span>
              <span>My Profile</span>
            </button>
          )}
          <button
            onClick={() => setHelpOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100/80 transition-colors duration-150"
            data-testid="sidebar-help-button"
          >
            <span className="relative w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
              <HelpCircle className="w-4 h-4" strokeWidth={1.75} />
              {isAdmin && setupProgress.remainingCore > 0 && !setupProgress.loading && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-blue-600 ring-2 ring-white"
                  data-testid="sidebar-help-setup-dot"
                  title={`${setupProgress.remainingCore} setup ${setupProgress.remainingCore === 1 ? 'step' : 'steps'} left`}
                />
              )}
            </span>
            <span>Help</span>
          </button>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors duration-150"
          >
            <span className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden lg:pt-0 pt-[calc(env(safe-area-inset-top,0px)+64px)]">
        {/* BookingFlows support session indicator (login-as from the platform admin) */}
        {userData.supportSession && (
          <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs sm:text-sm px-4 py-2 text-center flex-shrink-0 font-medium" data-testid="support-session-banner">
            BookingFlows support session — every action is audited
          </div>
        )}
        {/* Mobile Header - Fixed at top of viewport - visible height always 64px below safe area */}
        <div
          className="lg:hidden fixed top-0 left-0 right-0 flex items-center justify-between px-4 z-30"
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            height: 'calc(env(safe-area-inset-top, 0px) + 64px)',
            background: 'rgba(249,250,251,0.97)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? (
              <X className="w-7 h-7 text-gray-700" />
            ) : (
              <Menu className="w-7 h-7 text-gray-700" />
            )}
          </button>
          
          {/* Action Icons on Mobile - Top Right - Vertically Centered */}
          {(userType === 'admin' || userType === 'teamMember') && (
            <div className="flex items-center gap-3">
              <NotificationBell onViewRequests={handleViewRequests} />
            </div>
          )}
        </div>

        {/* Content Area - with scroll-padding for sticky header */}
        <div id="main-content" className="flex-1 overflow-y-auto scrollbar-hide scroll-pt-4">
          {/* Icon Bundle - Desktop Only (≥1024px) - Fixed at top right */}
          {(userType === 'admin' || userType === 'teamMember') && (
            <div className="hidden lg:flex fixed top-4 right-6 z-50">
              <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm shadow-lg rounded-full px-4 py-2">
                <NotificationBell onViewRequests={handleViewRequests} />
              </div>
            </div>
          )}
          
          <div className="px-4 sm:px-6 lg:px-8 pb-32 w-full lg:max-w-6xl lg:mx-auto pt-2 lg:pt-2">
            
            {/* Content */}
            <div className="lg:mt-20 mt-4">
            {activeView === 'assignments' && <AssignmentsTab />}
            {activeView === 'schedule' && (
              <CalendarScheduler 
                onRefresh={fetchData} 
                onDemoClick={handleSimulateDemo}
                demoLoading={demoLoading}
                showMyBookingsToggle={!isAdmin}
                teamMemberId={currentUserId}
                editBookingId={editBookingId}
                onEditBookingClear={() => setEditBookingId(null)}
                autoOpenAddBooking={autoOpenAddBooking}
                onAutoOpenAddBookingClear={() => setAutoOpenAddBooking(false)}
              />
            )}
            {activeView === 'overview' && (
              <HomeActivity onNavigate={handleNavigate} isAdmin={isAdmin}
                tabPermissions={tabPermissions} currentTeamId={currentUserId} />
            )}
            {activeView === 'home' && <OperationsDashboard onNavigate={handleNavigate} />}
            {activeView === 'marketplace' && <MarketplaceTab organizationId={organizationId} />}
            {activeView === 'distribution' && <DistributionTab organizationId={organizationId} />}
            {activeView === 'blog' && <BlogTab />}
            {activeView === 'conversions' && <ConversionsTab />}
            {activeView === 'advertising' && <AdvertisingTab />}
            {activeView === 'roles' && <RolesTab />}
            {activeView === 'teamMembers' && <TeamMembersTab teamMembers={teamMembers} onRefresh={fetchData} />}
            {activeView === 'assets' && <AssetsTab assets={assets} teamMembers={teamMembers} onRefresh={fetchData} />}
            {activeView === 'services' && <ServicesTabImproved onNavigate={setActiveView} />}
            {activeView === 'mySchedule' && <ScheduleTab />}
            {activeView === 'flows' && <MiniFlowsTab onNavigate={setActiveView} />}
            {activeView === 'profitLoss' && <ProfitLossTab onNavigate={setActiveView} />}
            {activeView === 'revenue' && <RevenueTab onNavigate={setActiveView} />}
            {activeView === 'expenses' && <ExpensesTab onNavigate={setActiveView} />}
            {activeView === 'reports' && <ReportsTab />}
            {activeView === 'discounts' && <DiscountsTab />}
            {activeView === 'giftCards' && <GiftCardsTab />}
            {activeView === 'account' && <ProfileTab />}
            {activeView === 'payments' && <PaymentsTabSimple onNavigate={setActiveView} />}
            {activeView === 'partners' && <PartnersTab organizationId={organizationId} />}
            {activeView === 'shared-services' && <SharedServicesTab />}
            {activeView === 'branding' && <BrandingTab />}
            {activeView === 'waivers' && <WaiversTabClean />}
            {activeView === 'notifications' && <NotificationsTab />}
            {activeView === 'reviews' && <ReviewsTab onNavigate={setActiveView} />}
            {activeView === 'relations' && <RelationsTab initialFilter={relationsFilter} key={relationsFilter} onNavigate={setActiveView} />}
            {activeView === 'checkin' && <CheckInTab />}
            {activeView === 'systemHealth' && <SystemHealthTabSimple />}
            </div>
          </div>
        </div>
      </main>

      <SettingsDialog 
        open={settingsOpen} 
        onOpenChange={setSettingsOpen}
      />

      <ApprovalReviewDialog
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        requests={pendingRequests}
        team_members={teamMembers}
        onApprovalComplete={handleApprovalComplete}
      />

      <HelpPage
        isOpen={helpOpen}
        onClose={() => {
          setHelpOpen(false);
          setHelpSection(null);
        }}
        initialSection={helpSection}
        onNavigate={(view) => {
          setActiveView(view);
        }}
        isAdmin={isAdmin}
        navGroups={navGroups}
      />

      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => {
          localStorage.setItem('hasSeenOnboarding', 'true');
          setShowOnboarding(false);
          toast.success("Welcome to BookingFlows! You're all set!");
        }}
      />

      {/* Setup Guide is now consolidated into the Help modal (sidebar → Help → Setup) */}

      {/* Guided tours: ✨ right-edge launcher + auto Basic tour for new operators */}
      <TourLauncher
        onOpenHelp={() => setHelpOpen(true)}
        prepareForTour={() => { setSidebarOpen(true); setCollapsedSections({}); }}
        hasAdvanced={isAdmin || hasAnyIntelAccess()}
        isAdmin={isAdmin}
        currentView={activeView}
        canAccessView={(view) => isAdmin || view === 'overview' || tabPermissions[view]?.read === true || tabPermissions[view] === true}
      />
      </div>
    </div>
      )}
    </>
  );
};

export default DashboardV2;
